import React, { useEffect, useState } from "react";
import { Plus, Trash2, KeyRound, CheckCircle2, XCircle, Layers } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "../../lib/api";
import { programs } from "../../mock/mock";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const PROGRAM_NAMES = programs.filter((p) => p.id !== "invitados").map((p) => p.name);

export default function ReferenciasAdmin() {
  const res = adminApi.referencias();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ codigo: "", programa: PROGRAM_NAMES[0], tipo: "reutilizable", activo: true });
  const [bulk, setBulk] = useState({ codigos: "", programa: PROGRAM_NAMES[0], tipo: "reutilizable" });

  const load = () => res.list().then(setItems).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const createOne = async () => {
    if (!form.codigo.trim()) { toast.error("Escribe un código."); return; }
    try {
      await res.create(form);
      toast.success("Referencia creada.");
      setOpen(false); setForm({ codigo: "", programa: PROGRAM_NAMES[0], tipo: "reutilizable", activo: true });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const createBulk = async () => {
    const codigos = bulk.codigos.split(/[\n,]+/).map((c) => c.trim()).filter(Boolean);
    if (codigos.length === 0) { toast.error("Agrega al menos un código."); return; }
    try {
      const r = await adminApi.bulkReferencias({ codigos, programa: bulk.programa, tipo: bulk.tipo });
      toast.success(`${r.creados} referencia(s) creadas.`);
      setBulkOpen(false); setBulk({ codigos: "", programa: PROGRAM_NAMES[0], tipo: "reutilizable" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleActive = async (it) => {
    try { await res.update(it.id, { activo: !it.activo }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    try { await res.remove(id); toast.success("Eliminada."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="referencias-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-[#0A1C30] flex items-center gap-2"><KeyRound className="w-6 h-6 text-[#E9A400]" /> Referencias Supera</h1>
          <p className="text-sm text-[#6B7688] mt-1">Solo quienes tengan una referencia autorizada podrán registrarse. Los invitados no usan referencia.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="bulk-ref-btn" onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-white ring-1 ring-black/10 text-[#0A1C30] font-bold px-4 py-2.5 text-sm hover:ring-[var(--gold)] transition-all">
            <Layers className="w-4 h-4" /> Cargar en lote
          </button>
          <button data-testid="add-ref-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-navy text-white font-bold px-4 py-2.5 text-sm hover:bg-[#0d2440] transition-colors">
            <Plus className="w-4 h-4" /> Nueva referencia
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F9] text-[#6B7688]">
            <tr>
              <th className="text-left px-4 py-3 font-bold">Código</th>
              <th className="text-left px-4 py-3 font-bold">Programa</th>
              <th className="text-left px-4 py-3 font-bold">Tipo</th>
              <th className="text-left px-4 py-3 font-bold">Estado</th>
              <th className="text-right px-4 py-3 font-bold">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {items.map((it) => (
              <tr key={it.id} data-testid={`ref-row-${it.codigo}`}>
                <td className="px-4 py-3 font-extrabold text-[#0A1C30]">{it.codigo}</td>
                <td className="px-4 py-3 text-[#2A3446]">{it.programa}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold ${it.tipo === "unico" ? "bg-[#FDECEC] text-[#C0392B]" : "bg-[#EAF7EF] text-[#22A860]"}`}>
                    {it.tipo === "unico" ? "Un solo uso" : "Reutilizable"}
                  </span>
                  {it.tipo === "unico" && it.usado && <span className="ml-2 text-xs text-[#6B7688]">usada por {it.usado_por}</span>}
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => toggleActive(it)} className="inline-flex items-center gap-1.5 font-bold text-xs">
                    {it.activo ? <><CheckCircle2 className="w-4 h-4 text-[#22A860]" /> Activa</> : <><XCircle className="w-4 h-4 text-[#B0B6C0]" /> Inactiva</>}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`del-ref-${it.codigo}`} onClick={() => remove(it.id)} className="p-2 rounded-lg hover:bg-[#FDECEC] text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6B7688]">Aún no hay referencias. Crea la primera para permitir registros.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Single create */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva referencia</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div><Label className="mb-1.5 block font-bold">Código</Label><Input data-testid="ref-codigo-input" value={form.codigo} onChange={(e) => setForm((f) => ({ ...f, codigo: e.target.value }))} placeholder="SUPERA-KIDS-001" /></div>
            <div><Label className="mb-1.5 block font-bold">Programa</Label>
              <Select value={form.programa} onValueChange={(v) => setForm((f) => ({ ...f, programa: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROGRAM_NAMES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block font-bold">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="reutilizable">Reutilizable</SelectItem><SelectItem value="unico">Un solo uso</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between"><Label className="font-bold">Activa</Label><Switch checked={form.activo} onCheckedChange={(v) => setForm((f) => ({ ...f, activo: v }))} /></div>
          </div>
          <DialogFooter><button data-testid="save-ref-btn" onClick={createOne} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-5 py-2.5">Crear</button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk create */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Cargar referencias en lote</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div><Label className="mb-1.5 block font-bold">Códigos (uno por línea o separados por coma)</Label>
              <textarea data-testid="bulk-codigos-input" value={bulk.codigos} onChange={(e) => setBulk((b) => ({ ...b, codigos: e.target.value }))} className="w-full min-h-[140px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" placeholder={"SUPERA-001\nSUPERA-002\nSUPERA-003"} />
            </div>
            <div><Label className="mb-1.5 block font-bold">Programa</Label>
              <Select value={bulk.programa} onValueChange={(v) => setBulk((b) => ({ ...b, programa: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PROGRAM_NAMES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block font-bold">Tipo</Label>
              <Select value={bulk.tipo} onValueChange={(v) => setBulk((b) => ({ ...b, tipo: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="reutilizable">Reutilizable</SelectItem><SelectItem value="unico">Un solo uso</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><button data-testid="save-bulk-btn" onClick={createBulk} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-5 py-2.5">Cargar</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
